<template>
  <a class="product-tile" :href="url" :id="id" tabindex="0" aria-live="polite">
    <div class="product-tile__badge-container">
      <span
        class="product-tile__badge badge"
        :style="`color: ${$store.state.badgeColorMap[productBadge][0]}; background-color: ${$store.state.badgeColorMap[productBadge][1]};`"
        v-if="productBadge"
      >{{ productBadge }}</span>
    </div>
    <div class="product-tile__images">
      <a :href="url">
        <img
          class="product-tile__img-main"
          :src="imageSize(images.default, '304x')"
          :alt="images.defaultAlt"
          :srcset="`${imageSize(images.default, '168x')} 168w, ${imageSize(images.default, '304x')} 304w, ${imageSize(images.default, '456x')} 1.5x`"
        />
        <img
          class="product-tile__img-hover"
          :src="imageSize(images.hover, '304x')"
          :alt="images.hoverAlt"
          :srcset="`${imageSize(images.hover, '168x')} 168w, ${imageSize(images.hover, '304x')} 304w, ${imageSize(images.hover, '456x')} 1.5x`"
        />
      </a>
    </div>
    <div class="product-tile__static-text-content">
      <div class="row">
        <div class="col-12-sm product-tile__title color-black">
          <span class="product-tile-title">{{ staticProductTitle }}</span>
        </div>
        <div class="col-12-sm product-tile__color color-off-black">
          <span class="product-tile-color body-sm">{{ staticProductColor }}</span>
        </div>
        <div
          class="col-12-sm product-tile__type color-grey"
          v-if="product.product_fit || product.product_type"
        >
          <span class="product-tile-type body-sm">{{ staticProductType }}</span>
        </div>
      </div>
      <div class="row">
        <div class="product-tile__price-container" v-if="!product.hidePrice">
          <span class="product-tile__compare-price" v-if="onSale">
            <s>{{ variant.compare_at_price | money }}</s>
          </span>
          <span
            class="product-tile__price"
            :class="{'color-util-sale' : onSale}"
          >{{ variant.price | money }}</span>
        </div>
      </div>
      <div class="row" v-if="product.promoMessage && product.promoMessage.length">
        <span class="product-tile__promo-message">{{ product.promoMessage }}</span>
      </div>
    </div>
  </a>
</template>

<script>
import {
  imageSize,
  imageCrop,
  badge,
  is3rdPartyProduct,
} from '../../helpers/util';
import { withId } from '../../helpers/props';
import uniqBy from 'lodash/uniqBy';
import Icon from '../basic/Icon.vue';

/**
 * @typedef ImageSource
 * @type {Object}
 * @prop {String} default
 * @prop {String} defaultAlt
 * @prop {String} hover
 * @prop {String} hoverAlt
 *
 * @typedef Buttons
 * @type {Object}
 * @prop {Boolean} showOptions
 * @prop {Boolean} showAtc
 */

export default {
  name: 'ProductTileB',
  components: {
    Icon,
  },
  data() {
    const state = {
      variant:
        this.selectedVariant ||
        this.product.selected_or_first_available_variant ||
        this.variants[0],
      loaded: false,
      // this feature wil eventually be added back. temporarily disabling it for now
      subtitlePretext:
        window.bvaccel.schema.productTile.subtitle_pretext && false,
      reviewSource: window.bvaccel && window.bvaccel.reviewSource,
    };
    return state;
  },
  props: {
    product: Object,
    selectedVariant: Object,
    onAddToCart: Function, // if not provided, the atc will not show
    collectionHandle: String, // used for linking to pdp with proper collection
    collectionGender: String, // used for linking to pdp for unisex products
    showMobileATC: {
      type: Boolean,
      default: false,
    },
    truncateTitle: {
      type: Boolean,
      default: true,
    },
    ...withId(),
  },
  methods: {
    imageSize,
    imageCrop,
    is3rdPartyProduct,
    truncate(title) {
      if (this.$mq === 'mobile') {
        if (title.length > 72) return `${title.substring(0, 72)}...`;
      }
      if (this.$mq !== 'mobile') {
        if (title.length > 72) return `${title.substring(0, 72)}...`;
      }
      return title;
    },
  },
  watch: {},
  computed: {
    selectOptionsVerbiage() {
      const { available } = this.variant;
      return available ? 'More Options' : 'Out of Stock';
    },

    subtitle() {
      // this feature wil eventually be added back. temporarily disabling it for now
      return false;

      const subtitlePretext = this.subtitlePretext;
      let cfVendor = this.product.cf_vendor;

      if (this.variant && this.variant.cf_vendor)
        cfVendor = this.variant.cf_vendor;

      if (subtitlePretext && cfVendor) return subtitlePretext + ' ' + cfVendor;
      else if (cfVendor) return cfVendor;
      return '';
    },

    /** @returns {String} */
    url() {
      const urlParts = [];

      if (!this.product.handle) {
        if (this.product.url) return this.product.url;
      }

      if (this.collectionHandle) {
        urlParts.push(`/collections/${this.collectionHandle}`);
      }

      urlParts.push(`/products/${this.product.handle}`);

      // only add variant if we're not the default
      if (
        this.product.variants &&
        this.product.variants.map((v) => v.id)[0] !== this.variant.id
      )
        urlParts.push(`?variant=${this.variant.id}`);

      const url = new URL(`${document.location.origin}${urlParts.join('')}`);

      /**
       * If collection is gendered, and product is unisex, apply gendered
       * query param for PDP functionality
       */
      if (this.product.tags.includes('Unisex') && this.collectionGender) {
        if (this.collectionGender === 'M') {
          url.searchParams.set('mens', true)
        } else if (this.collectionGender === 'F') {
          url.searchParams.set('womens', true)
        }
      }

      return url.href;
    },

    /** @returns {ImageSource} */
    images() {

      if (this.product.tags.includes('Unisex') && this.collectionGender) {
        // Unisex product in a gendered collection, filter images
        let filteredImages = [];

        if (this.collectionGender === 'F') {
          filteredImages = this.product.images.filter(img => !img.alt || !img.alt.split(' ').includes('mens'));
        } else if (this.collectionGender === 'M') {
          filteredImages = this.product.images.filter(img => !img.alt && !img.alt.split(' ').includes('womens'));
        }

        if (filteredImages.length) {
          const defaultImage = filteredImages[0]
          const defaultImageAlt = this.product.title;
          const nextImage = filteredImages[1]
            ? filteredImages[1]
            : filteredImages[0];
          const nextImageAlt = this.product.title;

          return {
            default: defaultImage.src,
            defaultAlt: defaultImageAlt,
            hover: nextImage.src,
            hoverAlt: nextImageAlt,
          };
        }
      }

      const defaultImage = this.product.images[0];
      const defaultImageAlt = this.product.title;
      const nextImage = this.product.images[1]
        ? this.product.images[1]
        : this.product.images[0];
      const nextImageAlt = this.product.title;

      return {
        default: defaultImage.src,
        defaultAlt: defaultImageAlt,
        hover: nextImage.src,
        hoverAlt: nextImageAlt,
      };
    },

    /** @returns {Boolean} */
    onSale() {
      return (
        this.variant &&
        /**
         * Important: We don't check for variant.available, because in
         * this case, variants just represent sizes.
         */
        this.variant.compare_at_price &&
        this.variant.price < this.variant.compare_at_price
      );
    },

    /** @returns {String} */
    productBadge() {
      return badge(this.product, this.onSale);
    },

    /** @returns {String} */
    staticProductTitle() {
      const t = this.product.title;
      const productTitle = t.substr(0, t.lastIndexOf('-'));
      return this.truncateTitle ? this.truncate(productTitle) : productTitle;
    },

    /** @returns {String} */
    staticProductColor() {
      return this.product.title.split('-').pop().trim();
    },

    staticProductType() {
      if (this.product.product_fit) return this.product.product_fit;

      return this.product.product_type;
    },
  },
};
</script>

<style scoped lang="scss">
.product-tile {
  position: relative;
  background-color: $color-white;
  user-select: none;
  display: flex;
  flex-direction: column;
  color: inherit;
  cursor: pointer;

  @include ie {
    display: block;
  }

  /* Fixes iOS :hover issue where it ignores click */
  @media (hover: hover) {
    &:hover &__img-hover,
    &:focus &__img-hover {
      opacity: 1;
    }
  }

  span:not(.button__content) {
    display: inline-block;
  }

  .row > * {
    margin-bottom: 0;
  }

  .button-row {
    position: absolute;
    bottom: 0;
    left: 0;
    padding: 0 8px;
    @include mobile-tablet {
      .button__content {
        font-size: 12px;
      }
    }
  }

  &__price {
    @include product-tile-price;
    line-height: 2em;
  }

  @media screen and (max-width: 400px) {
    max-width: 100%;
  }

  &:focus {
    // outline: none !important;
  }

  a {
    line-height: normal;
  }

  &-title {
    font-size: rem(14);
  }

  &-color {
    font-size: rem(12);
    line-height: 2em;
  }

  &__images {
    position: relative;
    font-size: 0;
    height: 0;
    width: 100%;
    padding-top: 150%;
    background-color: $color-white;
  }

  &__images a {
    font-size: 0;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  &__img-hover {
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
    transition: opacity 0.2s linear;
  }

  &__img-main {
    z-index: 2;
    position: relative;
    transition: none;
    transition: opacity 0.2s linear;
  }

  &__img-hover {
    z-index: 5;
  }

  &__img-main--placeholder {
    width: 100%;
    height: 100%;
    z-index: 4;
    fill: $color-lightest-grey;
  }

  &__img-main,
  &__img-hover {
    width: 100%;
    height: 100%;
    min-width: 100%;
    min-height: 100%;
    object-fit: cover;
  }

  &__static-text-content {
    background-color: $color-white;
  }

  &__img-main--placeholder {
    width: 100%;
    height: 100%;
  }

  &__compare-price {
    padding-right: 8px;
  }

  &__compare-price,
  &__compare-price s {
    @include product-tile-price;
  }
}
</style>
